import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import { type StudentOfficeHoursBooking } from '../../utils/sandboxOfficeHoursApi';
import {
    cancelMyStudentOfficeHourBooking,
    fetchMyStudentOfficeHourBookings,
    fetchTeacherOfficeHoursAvailability,
    requestMyStudentOfficeHourRefund,
    rescheduleMyStudentOfficeHourBooking,
} from '../../redux/sandbox/actions';

type Slot = { start: string; end: string };

type Props = {
    active: boolean;
    emptyHint?: string;
    onBook?: () => void;
};

function asList(res: unknown): StudentOfficeHoursBooking[] {
    if (Array.isArray(res)) return res;
    const data = (res as { data?: StudentOfficeHoursBooking[] })?.data;
    return Array.isArray(data) ? data : [];
}

function hostName(b: StudentOfficeHoursBooking) {
    const h = b.host;
    return [h?.firstName, h?.lastName].filter(Boolean).join(' ') || h?.userName || 'Mentor';
}

function teacherIdOf(b: StudentOfficeHoursBooking) {
    return String(b.host?._id || b.config?.owner || '');
}

function formatWhen(iso: string) {
    try {
        return new Intl.DateTimeFormat(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        }).format(new Date(iso));
    } catch {
        return iso;
    }
}

function todayYmd() {
    return new Date().toISOString().slice(0, 10);
}

/** Student session list + cancel/reschedule (embed in dialogs). */
export function SandboxMyOfficeHoursPanel({ active, emptyHint, onBook }: Props) {
    const dispatchPromise = useDispatchPromise();
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState<StudentOfficeHoursBooking[]>([]);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [rescheduleId, setRescheduleId] = useState<string | null>(null);
    const [date, setDate] = useState(todayYmd);
    const [slots, setSlots] = useState<Slot[]>([]);
    const [slotsLoading, setSlotsLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setRows(asList(await dispatchPromise(fetchMyStudentOfficeHourBookings())));
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not load bookings', 'error');
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [dispatchPromise]);

    useEffect(() => {
        if (active) {
            setRescheduleId(null);
            void load();
        }
    }, [active, load]);

    const rescheduleBooking = rows.find(r => r._id === rescheduleId) || null;

    useEffect(() => {
        if (!rescheduleBooking) {
            setSlots([]);
            return;
        }
        const tid = teacherIdOf(rescheduleBooking);
        if (!tid || !date) return;
        let cancelled = false;
        setSlotsLoading(true);
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        dispatchPromise(fetchTeacherOfficeHoursAvailability({ teacherId: tid, date, timezone: tz }))
            .then((res: any) => {
                if (!cancelled) setSlots(res?.slots || []);
            })
            .catch(() => {
                if (!cancelled) setSlots([]);
            })
            .finally(() => {
                if (!cancelled) setSlotsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [rescheduleBooking, date, dispatchPromise]);

    const cancelBooking = async (booking: StudentOfficeHoursBooking) => {
        setBusyId(booking._id);
        try {
            await dispatchPromise(cancelMyStudentOfficeHourBooking({ bookingId: booking._id }));
            showToastMessage('Session cancelled', 'success');
            await load();
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not cancel', 'error');
        } finally {
            setBusyId(null);
        }
    };

    const refundBooking = async (booking: StudentOfficeHoursBooking) => {
        setBusyId(booking._id);
        try {
            await dispatchPromise(
                requestMyStudentOfficeHourRefund({ bookingId: booking._id, reason: 'Mentor did not attend' }),
            );
            showToastMessage('Refund requested — you’ll be reimbursed shortly', 'success');
            await load();
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not request refund', 'error');
        } finally {
            setBusyId(null);
        }
    };

    const canRefundNoShow = (b: StudentOfficeHoursBooking) => {
        if (b.payment?.status !== 'paid') return false;
        if (b.payment?.teacher_credited) return false;
        if (b.status === 'no_show') return false;
        if (b.status !== 'confirmed' && b.status !== 'completed') return false;
        const started = new Date(b.start_time).getTime() <= Date.now();
        if (!started) return false;
        const holdEnd = b.payment?.payout_eligible_at
            ? new Date(b.payment.payout_eligible_at).getTime()
            : new Date(b.end_time).getTime() + 7 * 24 * 60 * 60 * 1000;
        return Date.now() <= holdEnd;
    };

    const pickSlot = async (slot: Slot) => {
        if (!rescheduleId) return;
        setBusyId(rescheduleId);
        try {
            await dispatchPromise(
                rescheduleMyStudentOfficeHourBooking({
                    bookingId: rescheduleId,
                    body: {
                        start_time: slot.start,
                        date,
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    },
                }),
            );
            showToastMessage('Session rescheduled', 'success');
            setRescheduleId(null);
            await load();
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not reschedule', 'error');
        } finally {
            setBusyId(null);
        }
    };

    const upcoming = rows.filter(
        b => b.status === 'confirmed' && new Date(b.start_time).getTime() >= Date.now() - 60 * 1000,
    );
    const pending = rows.filter(b => b.status === 'pending_payment');
    const past = rows.filter(
        b =>
            b.status === 'cancelled' ||
            b.status === 'completed' ||
            b.status === 'no_show' ||
            new Date(b.start_time).getTime() < Date.now(),
    );

    const renderRow = (b: StudentOfficeHoursBooking, actions: boolean) => (
        <Box
            key={b._id}
            sx={{
                p: 1.5,
                mb: 1,
                borderRadius: 2,
                bgcolor: OBSIDIAN.surfaceContainer,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1.5,
                alignItems: 'flex-start',
            }}>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: 13,
                        color: OBSIDIAN.onSurface,
                    }}>
                    {b.config?.title || 'Office hours'} · {hostName(b)}
                </Typography>
                <Typography sx={{ fontSize: 12, color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                    {formatWhen(b.start_time)}
                    {b.status !== 'confirmed' ? ` · ${b.status.replace('_', ' ')}` : ''}
                </Typography>
                {(b.project_title || b.project?.title) && (
                    <Typography sx={{ mt: 0.5, fontSize: 12, color: OBSIDIAN.primary, fontWeight: 700 }}>
                        Project: {b.project_title || b.project?.title}
                    </Typography>
                )}
                {b.notes ? (
                    <Typography
                        sx={{
                            mt: 0.35,
                            fontSize: 12,
                            color: OBSIDIAN.onSurfaceVariant,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            whiteSpace: 'pre-wrap',
                        }}>
                        Notes: {b.notes}
                    </Typography>
                ) : null}
                {b.meet_link && b.status === 'confirmed' ? (
                    <Button
                        href={b.meet_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            mt: 0.5,
                            px: 0,
                            minWidth: 0,
                            textTransform: 'none',
                            color: OBSIDIAN.secondary,
                            fontWeight: 700,
                        }}>
                        Join Meet
                    </Button>
                ) : null}
            </Box>
            {actions ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                    {b.status === 'confirmed' ? (
                        <Button
                            disabled={busyId === b._id}
                            onClick={() => {
                                setDate(todayYmd());
                                setRescheduleId(b._id);
                            }}
                            sx={{ textTransform: 'none', color: OBSIDIAN.primary, fontWeight: 700, fontSize: 12 }}>
                            Reschedule
                        </Button>
                    ) : null}
                    {(b.status === 'confirmed' || b.status === 'pending_payment') && (
                        <Button
                            disabled={busyId === b._id}
                            onClick={() => void cancelBooking(b)}
                            sx={{ textTransform: 'none', color: OBSIDIAN.onSurfaceVariant, fontSize: 12 }}>
                            Cancel
                        </Button>
                    )}
                </Box>
            ) : canRefundNoShow(b) ? (
                <Button
                    disabled={busyId === b._id}
                    onClick={() => void refundBooking(b)}
                    sx={{
                        textTransform: 'none',
                        color: OBSIDIAN.primary,
                        fontWeight: 700,
                        fontSize: 12,
                        flexShrink: 0,
                    }}>
                    Request refund
                </Button>
            ) : b.status === 'no_show' || b.payment?.status === 'refunded' ? (
                <Typography sx={{ fontSize: 11, color: OBSIDIAN.onSurfaceVariant, flexShrink: 0 }}>Refunded</Typography>
            ) : null}
        </Box>
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: OBSIDIAN.primary }} />
            </Box>
        );
    }

    if (rescheduleBooking) {
        return (
            <Box>
                <Typography sx={{ mb: 1, fontSize: 13, color: OBSIDIAN.onSurfaceVariant }}>
                    Pick a new time with {hostName(rescheduleBooking)}
                </Typography>
                <TextField
                    type="date"
                    size="small"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                    sx={{
                        mb: 1.5,
                        '& .MuiOutlinedInput-root': {
                            color: OBSIDIAN.onSurface,
                            bgcolor: OBSIDIAN.surfaceContainer,
                        },
                        '& input': { colorScheme: 'dark' },
                    }}
                />
                {slotsLoading ? (
                    <CircularProgress size={22} sx={{ color: OBSIDIAN.primary }} />
                ) : slots.length ? (
                    slots.map(slot => (
                        <Button
                            key={slot.start}
                            disabled={busyId === rescheduleId}
                            onClick={() => void pickSlot(slot)}
                            sx={{
                                display: 'block',
                                width: '100%',
                                mb: 0.75,
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                color: OBSIDIAN.onSurface,
                                bgcolor: OBSIDIAN.surfaceContainer,
                                borderRadius: 2,
                            }}>
                            {formatWhen(slot.start)}
                        </Button>
                    ))
                ) : (
                    <Typography sx={{ fontSize: 13, color: OBSIDIAN.onSurfaceVariant }}>
                        No open slots on this date.
                    </Typography>
                )}
                <Button
                    onClick={() => setRescheduleId(null)}
                    sx={{ mt: 1.5, textTransform: 'none', color: OBSIDIAN.primary }}>
                    Back
                </Button>
            </Box>
        );
    }

    if (!rows.length) {
        return (
            <Box>
                <Typography sx={{ color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT }}>
                    {emptyHint || 'No sessions booked yet.'}
                </Typography>
                {onBook ? (
                    <Button
                        onClick={onBook}
                        sx={{
                            mt: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            color: OBSIDIAN.onPrimaryFixed,
                            bgcolor: OBSIDIAN.primary,
                            borderRadius: 999,
                            px: 2,
                            '&:hover': { filter: 'brightness(1.05)' },
                        }}>
                        Book a session
                    </Button>
                ) : null}
            </Box>
        );
    }

    return (
        <>
            {pending.length ? (
                <>
                    <Typography
                        sx={{
                            mb: 1,
                            fontSize: 11,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.onSurfaceVariant,
                        }}>
                        Awaiting payment
                    </Typography>
                    {pending.map(b => renderRow(b, true))}
                </>
            ) : null}
            <Typography
                sx={{
                    mb: 1,
                    mt: pending.length ? 2 : 0,
                    fontSize: 11,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: OBSIDIAN.onSurfaceVariant,
                }}>
                Upcoming
            </Typography>
            {upcoming.length ? (
                upcoming.map(b => renderRow(b, true))
            ) : (
                <Typography sx={{ mb: 2, fontSize: 13, color: OBSIDIAN.onSurfaceVariant }}>None upcoming.</Typography>
            )}
            {past.length ? (
                <>
                    <Typography
                        sx={{
                            mb: 1,
                            mt: 2,
                            fontSize: 11,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.onSurfaceVariant,
                        }}>
                        Past
                    </Typography>
                    {past
                        .filter(b => b.status !== 'confirmed' || new Date(b.start_time).getTime() < Date.now())
                        .slice(0, 8)
                        .map(b => renderRow(b, false))}
                </>
            ) : null}
        </>
    );
}
